const reportedPreparationDiagnostics = new Set()

function safeOverview(value) {
  return value && typeof value === 'object' ? value : {}
}

export function hasCompletedPersonaGeneration(group = {}) {
  const currentGroup = safeOverview(group)
  const coveredApps = Number(currentGroup.covered_apps || 0)
  const generatedTasks = Number(currentGroup.png_ready_tasks || 0)
  return coveredApps > 0 && generatedTasks >= coveredApps
}

export function isRecoveringPreparation(group = {}) {
  const currentGroup = safeOverview(group)
  return currentGroup.copy_status !== 'ready'
    && currentGroup.error?.retryable === true
    && currentGroup.error?.recovery_action !== 'repair_rule_upgrade'
    && !hasCompletedPersonaGeneration(currentGroup)
}

export function shouldPresentPreparationFailure(group = {}) {
  const currentGroup = safeOverview(group)
  return Boolean(currentGroup.error)
    && currentGroup.copy_status !== 'ready'
    && !isRecoveringPreparation(currentGroup)
    && !hasCompletedPersonaGeneration(currentGroup)
}

export function contentProgressPercent(progress = {}) {
  const total = Number(progress.total_unit_count ?? progress.content_total_units ?? 0)
  const passed = Number(progress.passed_unit_count ?? progress.content_passed_units ?? 0)
  if (!total || progress.details_complete === false) return null
  return Math.max(0, Math.min(100, Math.round((passed / total) * 100)))
}

export function contentProgressText(progress = {}) {
  const total = Number(progress.total_unit_count ?? progress.content_total_units ?? 0)
  const passed = Number(progress.passed_unit_count ?? progress.content_passed_units ?? 0)
  if (!total || progress.details_complete === false) return '进度详情待同步'
  return passed === total ? '内容准备完成' : '已通过 ' + passed + '/' + total + ' 个内容部分'
}

export function contentProgressLabel(progress = {}) {
  const percent = contentProgressPercent(progress)
  return contentProgressText(progress) + (percent === null ? '' : ` · ${percent}%`)
}

export function contentProgressStageLabel(progress = {}) {
  const unitLabels = { post: '正文', page_1: '封面', page_2: '图二说明', page_3: '图三说明' }
  const stage = String(progress.current_stage || '')
  const currentUnit = unitLabels[progress.current_unit] || ''
  if (stage === 'simple_material_evidence_wait') return '等待补充素材说明'
  if (progress.execution_state === 'needs_attention') return '等待必要条件恢复'
  if (progress.execution_state === 'waiting_retry' || stage === 'simple_draft_waiting_retry') return '已保留进度，等待自动继续'
  if (progress.model_repair?.active === true) return 'DeepSeek 正在修复' + (currentUnit || '内容')
  if (progress.execution_state === 'repairing' && progress.model_repair?.status === 'response_saved') return '正在复验' + (currentUnit || '修复结果')
  if (stage.includes('relation')) return '正在复核图文关联'
  if (stage.includes('repair') && currentUnit) {
    const attempt = Number(progress.repair_attempt || 0)
    return '正在修复' + currentUnit + (attempt ? ' · 第 ' + attempt + ' 次' : '')
  }
  if (stage.includes('partial')) return '正在补齐缺失内容'
  if (stage.includes('saved')) return '正在检查内容'
  return stage === 'ready' ? '内容检查已通过' : '正在准备内容'
}

export function contentProgressFieldStatuses(progress = {}) {
  const unitLabels = { post: '正文', page_1: '封面', page_2: '图二说明', page_3: '图三说明' }
  const fields = progress.field_statuses && typeof progress.field_statuses === 'object'
    ? progress.field_statuses : {}
  const issues = Array.isArray(progress.unresolved_issues) ? progress.unresolved_issues : []
  return Object.entries(unitLabels).filter(([field]) => fields[field]).map(([field, label]) => {
    const status = String(fields[field] || 'pending_validation')
    const issue = issues.find(item => item?.field === field)
    const issueLabels = {
      short_explanation_missing: '说明待补充', photo_explanation_repeats_heading: '说明需要展开',
      photo_points_repeated: '要点重复', photo_pages_repeated: '两图重点重复',
      photo_fact_reference_invalid: '卖点依据待核对',
      material_relation_unconfirmed: '关联待确认', material_evidence_insufficient: '素材说明不足', verified_fact_missing: '事实缺失',
      forbidden_claim: '表达不合规', public_solicitation_forbidden: '表达不合规',
      internal_copy_marker_forbidden: '表达不合规', relationship_evidence_forbidden: '体验依据不足',
      relationship_evidence_missing: '体验依据不足',
    }
    const statusLabel = progress.execution_state === 'repairing' && progress.current_unit === field
      ? '修复中'
      : status === 'passed' ? '已通过'
        : issueLabels[issue?.code] || (status === 'needs_repair' ? '需要修复' : '待检查')
    return { field, label, status, statusLabel }
  })
}

export function batchPreparationHeadline(overview = {}) {
  const currentOverview = safeOverview(overview)
  if (currentOverview.batch_status === 'stopped') return '本批任务已停止'
  const total = Number(currentOverview.metrics?.covered_apps || 0)
  if (currentOverview.batch_status === 'completed' && total) return `${total} 位用户任务成功，三图回执已确认`
  const generated = Number(currentOverview.metrics?.png_ready_tasks || 0)
  if (total && generated) {
    return generated === total
      ? `${total} 位用户图文已生成，后续发布分别记录`
      : `${total} 位用户：${generated} 位已确认生成，${total - generated} 位尚未收到生成确认`
  }
  const groups = Array.isArray(currentOverview.persona_groups) ? currentOverview.persona_groups : []
  if (groups.some(group => group?.error?.recovery_action === 'repair_rule_upgrade')) {
    return '旧任务可按新版校验规则继续准备'
  }
  if (groups.length && groups.every(group => group.copy_status === 'ready' || isRecoveringPreparation(group))) {
    if (groups.some(group => group.copy_status === 'failed' || group.copy_status === 'retry_wait')) {
      return '已保留任务，系统将自动继续准备'
    }
  }
  if (currentOverview.batch_status === 'failed' || Number(currentOverview.metrics?.failed_personas) > 0
      || groups.some(group => group?.copy_status === 'failed')) {
    return '云端内容准备受阻 · 需要项目经理处理'
  }
  const repairing = groups.filter(group => String(group?.content_progress?.current_stage || '').includes('repair'))
  if (repairing.length) return repairing.length + ' 个人设正在修复内容，其余人设独立继续'
  const pendingGroups = groups.filter(group => group?.copy_status && group.copy_status !== 'ready')
  const executionStates = pendingGroups.map(group => group?.error?.execution_state)
  if (pendingGroups.length && executionStates.every(state => state === 'waiting_retry')) {
    return '已保存进度，等待自动恢复'
  }
  if (pendingGroups.length && executionStates.every(state => state === 'waiting_worker')) {
    return '已保存进度，等待内容服务领取'
  }
  if (executionStates.includes('worker_stale')) {
    return '内容服务等待恢复，已完成内容已保存'
  }
  return currentOverview.status_label || '正在读取内容准备状态'
}

export function canRetryCloudPreparation(overview = {}) {
  // Normal recoveries are owned by the worker.  This button exists only for a
  // persisted package whose old repair rule is explicitly upgradeable.
  const groups = Array.isArray(safeOverview(overview).persona_groups) ? safeOverview(overview).persona_groups : []
  return groups.some(group => !hasCompletedPersonaGeneration(group)
    && group?.error?.recovery_action === 'repair_rule_upgrade')
}

function safeContentRepairReason(error, fallback) {
  const labels = {
    material_relation_unconfirmed: '图文关联尚未确认', verified_fact_missing: '缺少已核验事实依据',
    forbidden_claim: '包含未确认的明确承诺', public_solicitation_forbidden: '包含不适合公开发布的引导表达',
    internal_copy_marker_forbidden: '包含内部标记', relationship_evidence_forbidden: '体验口吻不符合当前人设依据',
    relationship_evidence_missing: '体验口吻缺少关系依据',
  }
  const issues = Array.isArray(error?.validation_issues) ? error.validation_issues : []
  const safeLabels = [...new Set(issues.map(issue => labels[issue?.code]).filter(Boolean))]
  if (safeLabels.length) return safeLabels.join('；')
  const reason = String(fallback || '')
  if (reason && !/(ValueError|simple_|material_relation_missing)/i.test(reason)) return reason
  return '图文关联尚未确认；这不是对图片本身不相关的判定。'
}

export function reportPreparationDiagnostics(overview = {}, reporter = console) {
  const currentOverview = safeOverview(overview)
  const groups = Array.isArray(currentOverview.persona_groups) ? currentOverview.persona_groups : []
  return groups.flatMap((group) => {
    const error = group?.error
    if ((!shouldPresentPreparationFailure(group) && !isRecoveringPreparation(group))
        || !error?.diagnostic_id || !error?.preparation_stage) return []
    const events = Array.isArray(error.diagnostic_events)
      ? error.diagnostic_events.filter(event => event?.success === false)
      : []
    const failures = events.length ? events : [{
      sequence: 'final', phase: error.preparation_stage, event: 'final_failure',
      detail: error.failure_reason || null,
    }]
    return failures.flatMap((event, index) => {
      const stage = event.phase || error.preparation_stage
      const sequence = event.sequence ?? index + 1
      const key = [group.persona_id || 'unknown', error.diagnostic_id, sequence, stage].join(':')
      if (reportedPreparationDiagnostics.has(key)) return []
      reportedPreparationDiagnostics.add(key)
      const rawReason = event.detail || error.failure_reason || null
      const diagnostic = {
        source: 'cloud', persona_id: group.persona_id || null, diagnostic_id: error.diagnostic_id,
        sequence, stage, stage_label: error.preparation_stage_label || '内容准备',
        event: event.event || 'contract_checked', failed_field: error.failed_field || null,
        reason: error.category === 'content_repair_required' || error.category === 'content_repair_rule_upgrade'
          ? safeContentRepairReason(error, rawReason) : rawReason,
        retryable: error.retryable === true,
        ...(error.recovery_source ? {
          recovery_source: error.recovery_source,
          content_changed: error.content_changed === true,
          consecutive_no_progress: Number(error.consecutive_no_progress || 0),
          next_action: error.recovery_next_action || null,
        } : {}),
      }
      const text = [
        '诊断编号=' + diagnostic.diagnostic_id, '步骤=' + diagnostic.stage,
        diagnostic.failed_field ? '位置=' + diagnostic.failed_field : '',
        diagnostic.reason ? '原因=' + diagnostic.reason : '',
        diagnostic.recovery_source ? '来源=' + diagnostic.recovery_source : '',
        diagnostic.next_action ? '下一处理=' + diagnostic.next_action : '',
        '是否重试=' + (diagnostic.retryable ? '是' : '否'),
      ].filter(Boolean).join('；')
      reporter.warn('AI营销项目经理任务诊断：' + text, diagnostic)
      return [diagnostic]
    })
  })
}
export function dispatchRunLabel(run) {
  if (['retry_wait', 'failed'].includes(run?.state) && run?.error_code === 'GENERATION_INPUT_MISSING') {
    return '等待素材说明，补齐后自动核对'
  }
  return ({
    DISPATCHED: '已自动下发', ALREADY_DISPATCHED: '当天任务已存在',
    NO_ACTIVE_BINDINGS: '没有有效手机', NO_ACTIVE_MANAGER: '没有有效项目经理',
    CONFIG_INVALID: '设置或素材不完整', RUN_FAILED: '执行失败',
  })[run?.reason_code] || run?.state || '等待执行'
}

export function preparationTimingText(group = {}) {
  const timing = group.timing || group.content_progress?.timing
  if (!timing) return '暂无精确阶段耗时'
  const phases = timing.model_phase_seconds || {}
  const seconds = value => Math.max(0, Math.round(Number(value) || 0)) + '秒'
  const prefix = timing.history_complete === false ? '记录以来：' : ''
  return prefix + '生成 ' + seconds(phases.generation) + ' · 复核 ' + seconds(phases.review)
    + ' · 修复 ' + seconds(phases.repair)
}
